import React, { useState, useEffect } from 'react';
import { Modal } from '../../design-system';
import { BUTTON_PRESETS } from '../../design-system/constants';
import { Form, Input, Select, message } from 'antd';
import ApiServiceNode from '../../service/ApiServiceNode';
import logger from '../../utils/logger';



const { TextArea } = Input;

const buildCurrencyOptions = (currencies) => {
    const optionsByIso = new Map();

    (Array.isArray(currencies) ? currencies : []).forEach((currency) => {
        const rawIso = typeof currency === 'string' ? currency : currency?.iso;
        const iso = String(rawIso || '').trim().toUpperCase();
        if (!iso || optionsByIso.has(iso)) {
            return;
        }

        const name = typeof currency === 'string' ? '' : String(currency?.name || '').trim();
        optionsByIso.set(iso, {
            iso,
            label: name ? `${iso} - ${name}` : iso,
        });
    });

    return Array.from(optionsByIso.values()).sort((a, b) => a.iso.localeCompare(b.iso));
};

const BankFormModal = ({ visible, bank, officeId, currencies = [], onClose }) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const currencyOptions = buildCurrencyOptions(currencies);

    useEffect(() => {
        if (visible) {
            if (bank) {
                form.setFieldsValue({
                    name: bank.name,
                    account_name: bank.account_name,
                    account_number: bank.account_number,
                    branch_name: bank.branch_name,
                    branch_code: bank.branch_code,
                    swift_code: bank.swift_code,
                    currency: bank.currency,
                    address: bank.address,
                    notes: bank.notes
                });
            } else {
                form.resetFields();
            }
        }
    }, [visible, bank, form]);

    const handleSave = async () => {
        try {
            const values = await form.validateFields();
            setLoading(true);

            const bankData = {
                ...values,
                office_id: officeId
            };

            if (bank) {
                await ApiServiceNode.put(`/settings/offices/banks/${bank.id}`, bankData);
                message.success('Bank account updated successfully');
            } else {
                await ApiServiceNode.post('/settings/offices/banks', bankData);
                message.success('Bank account created successfully');
            }

            onClose(true); // Refresh list
        } catch (error) {
            logger.error('Error saving bank account:', error);
            message.error('Failed to save bank account');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal visible={visible} onClose={() => onClose(false)} width="large">
            <Modal.Header title={bank ? 'Edit Bank Account' : 'Add Bank Account'} />
            <Modal.Body padding="default">
                <Form form={form} layout="vertical">
                    <Form.Item
                        label="Bank Name"
                        name="name"
                        rules={[
                            { required: true, message: 'Please enter bank name' },
                            { max: 128, message: 'Maximum 128 characters' }
                        ]}
                    >
                        <Input placeholder="e.g., Maybank" maxLength={128} />
                    </Form.Item>

                    <Form.Item
                        label="Account Name"
                        name="account_name"
                        rules={[
                            { required: true, message: 'Please enter account name' },
                            { max: 128, message: 'Maximum 128 characters' }
                        ]}
                    >
                        <Input placeholder="e.g., Company Name Sdn Bhd" maxLength={128} />
                    </Form.Item>

                    <Form.Item
                        label="Account Number"
                        name="account_number"
                        rules={[
                            { required: true, message: 'Please enter account number' },
                            { max: 64, message: 'Maximum 64 characters' }
                        ]}
                    >
                        <Input placeholder="e.g., 1234567890" maxLength={64} />
                    </Form.Item>

                    <Form.Item
                        label="Branch Name"
                        name="branch_name"
                        rules={[{ max: 128, message: 'Maximum 128 characters' }]}
                    >
                        <Input placeholder="e.g., Kuala Lumpur Main Branch" maxLength={128} />
                    </Form.Item>

                    <Form.Item
                        label="Branch Code"
                        name="branch_code"
                        rules={[{ max: 64, message: 'Maximum 64 characters' }]}
                    >
                        <Input placeholder="e.g., 12345" maxLength={64} />
                    </Form.Item>

                    <Form.Item
                        label="SWIFT/BIC Code"
                        name="swift_code"
                        rules={[{ max: 32, message: 'Maximum 32 characters' }]}
                    >
                        <Input placeholder="e.g., MBBEMYKL" maxLength={32} />
                    </Form.Item>

                    <Form.Item
                        label="Currency"
                        name="currency"
                    >
                        <Select placeholder="Select currency" allowClear showSearch optionFilterProp="label">
                            {currencyOptions.map((currency) => (
                                <Select.Option
                                    key={currency.iso}
                                    value={currency.iso}
                                    label={currency.label}
                                >
                                    {currency.label}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        label="Bank Address"
                        name="address"
                        rules={[{ max: 255, message: 'Maximum 255 characters' }]}
                    >
                        <TextArea
                            placeholder="Bank address"
                            rows={3}
                            maxLength={255}
                        />
                    </Form.Item>

                    <Form.Item
                        label="Notes"
                        name="notes"
                        rules={[{ max: 255, message: 'Maximum 255 characters' }]}
                    >
                        <TextArea
                            placeholder="Additional notes"
                            rows={3}
                            maxLength={255}
                        />
                    </Form.Item>
                </Form>
            </Modal.Body>
            <Modal.Footer buttons={[
                {
                    ...BUTTON_PRESETS.SAVE,
                    label: bank ? 'Update' : 'Create',
                    loading,
                    onClick: handleSave
                }
            ]} />
        </Modal>
    );
};

export default BankFormModal;
